/**
 * Empty / unavailable TN inventory messaging for Seatics listing embeds.
 * Keep traffic errors distinct — those must not use this copy or trigger close.
 */

import { listingEventUnixFromPublicData } from './listingEventDate';

export const TN_EMPTY_INVENTORY_CUSTOMER_MESSAGE =
  'Currently sold out. Check back later for additional tickets.';

export const TN_EVENT_DATE_PASSED_CUSTOMER_MESSAGE = 'This event date has passed.';

export const TN_SEARCH_MORE_EVENTS_CTA_LABEL = 'Search for more events';

/** UTC start-of-today in unix seconds — same day boundary the auto-close cron uses. */
export const utcStartOfTodayUnix = (now = Date.now()) => {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000;
};

/**
 * Purchase-unavailable states knowable at render time without any TN API call.
 *
 * 'past'   — the event date is before today (UTC), matching autoCloseListing cron
 *            semantics. Checked first: a closed past-date listing should say
 *            "date has passed", not "sold out".
 * 'closed' — the Flex listing is closed but the event is still upcoming (manual
 *            close, duplicate close, etc.) → treat as sold out; inventory may
 *            exist on another listing or return later.
 * null     — listing is purchasable as far as we can tell without inventory data.
 */
export const tnListingUnavailableReason = (listing, now = Date.now()) => {
  const attributes = listing?.attributes || {};
  const eventUnix = listingEventUnixFromPublicData(attributes.publicData || {});
  if (eventUnix != null && eventUnix < utcStartOfTodayUnix(now)) {
    return 'past';
  }
  if (attributes.state === 'closed') {
    return 'closed';
  }
  return null;
};

/**
 * Classify a Seatics availability API result for map embed UI.
 * @param {{ ok: boolean, status: number, data?: object|null }} result
 * @param {(status: number, data?: object) => boolean} [isTrafficError]
 * @returns {'ready'|'empty'|'trafficError'|'error'}
 */
export const classifyTnAvailabilityOutcome = (result, isTrafficError) => {
  const { ok, status, data } = result || {};
  if (!ok) {
    if (typeof isTrafficError === 'function' && isTrafficError(status, data || {})) {
      return 'trafficError';
    }
    return 'error';
  }
  const tgs = Array.isArray(data?.ticketGroups) ? data.ticketGroups : [];
  return tgs.length ? 'ready' : 'empty';
};
